import { firebaseAuth } from '../../lib/firebase';
import { ApiError } from '../../lib/api';
import { NonJsonResponseError, type BulkRunStepResponse } from './bulkRunDrive';
import type {
  AccountOnboarding,
  OnboardingBatchItem,
  OnboardingBatchResult,
  OnboardingPageOption,
} from '../../types/accountOnboarding';
import type {
  LeadFormSummary,
  PageSummary,
  PixelSummary,
} from '../../types/metaAdResources';
import type { Account } from '../../types/account';
import type {
  BulkMappingRow,
  BulkRunStatus,
  BulkSessionStatus,
  MasterAdAccount,
  MasterPage,
} from '../../types/bulkOnboarding';
import type {
  GbpAgencyLocation,
  GbpAssignResult,
  GbpMappingAccount,
  GbpTokenHolder,
} from '../../types/gbpOnboarding';
import type {
  GoogleAdsAccountListResult,
  GoogleAdsSelectResult,
} from '../../lib/googleAdsConnect';

// API client for the Account Onboarding wizard. Uses a self-contained fetch
// (publishDraft precedent) because the shared api() helper drops everything
// but `error`/`missing` from an error body — and the PAYMENT_REQUIRED 402
// carries structured reason/details the wizard's blocking banner needs.

export class PaymentRequiredError extends Error {
  constructor(
    message: string,
    public readonly reason: string,
    public readonly details: Record<string, unknown> | null,
  ) {
    super(message);
    this.name = 'PaymentRequiredError';
  }
}

// The headless connect window lapsed — the wizard routes the row back to
// Connect with the explanatory message.
export class TokenExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TokenExpiredError';
  }
}

async function onboardingFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // Wait for Firebase to finish restoring the persisted session before reading
  // currentUser — on a COLD load (e.g. returning from the OAuth redirect to the
  // callback page) currentUser is null until rehydration from IndexedDB, which
  // made recordCallback throw a spurious 401 before the POST ever fired. A
  // genuinely signed-out user still has currentUser === null after this
  // resolves, so the 401 below stays correct.
  await firebaseAuth.authStateReady();
  const user = firebaseAuth.currentUser;
  if (!user) throw new ApiError(401, 'Not signed in');
  const token = await user.getIdToken();

  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${token}`,
    },
  });
  if (res.ok) return res.json() as Promise<T>;

  // ⚠️ An error with NO JSON body did not come from our API — it is a proxy's
  // error page (Cloudflare's 524, a Cloud Run 502). That means the answer was
  // LOST, not that the request failed; callers that can re-read state (the bulk
  // run loop) must be able to tell the two apart. See bulkRunDrive.ts.
  let parsed: unknown;
  try {
    parsed = await res.json();
  } catch {
    throw new NonJsonResponseError(res.status);
  }
  const body = (parsed ?? {}) as {
    error?: string;
    code?: string;
    reason?: string;
    details?: Record<string, unknown> | null;
  };
  if (res.status === 402 && body.code === 'PAYMENT_REQUIRED') {
    throw new PaymentRequiredError(
      body.error ?? 'Zernio account limit reached.',
      body.reason ?? 'unknown',
      body.details ?? null,
    );
  }
  if (res.status === 410 && body.code === 'token_expired') {
    throw new TokenExpiredError(body.error ?? 'Connection window expired — re-run Connect Meta.');
  }
  throw new ApiError(res.status, body.error ?? `HTTP ${res.status}`);
}

export type OnboardingAdAccount = {
  id: string; // act_…
  name: string;
  currency: string | null;
  status: string | null;
};

export type OnboardingResources = {
  pages: PageSummary[] | null;
  pixels: PixelSummary[] | null;
  leadForms: LeadFormSummary[] | null;
  instagram: { id: string; username: string | null } | null;
  errors: { pages: string | null; pixels: string | null; leadForms: string | null };
};

export type FinalizeBody = {
  adAccountId?: string | null;
  pageId?: string | null;
  pageName?: string | null;
  instagramAccountId?: string | null;
  instagramAccountName?: string | null;
  pixelId?: string | null;
  pixelName?: string | null;
  defaultLeadFormId?: string | null;
  leadFormName?: string | null;
};

export function listOnboardings(): Promise<{ onboardings: AccountOnboarding[] }> {
  return onboardingFetch('/api/onboarding');
}

export function createOnboardingProfile(
  item: OnboardingBatchItem,
): Promise<{ onboarding: AccountOnboarding; resumed: boolean }> {
  return onboardingFetch('/api/onboarding/profiles', {
    method: 'POST',
    body: JSON.stringify(item),
  });
}

export function createOnboardingBatch(
  items: OnboardingBatchItem[],
): Promise<{ results: OnboardingBatchResult[] }> {
  return onboardingFetch('/api/onboarding/profiles/batch', {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
}

// Reopen the onboarder against an already-connected account (reusing its
// existing Zernio profile). Returns the fresh reconnect record; the wizard
// opens it at the right step. Phase 1: mode 'ad-account'.
export function reconnectOnboarding(
  accountId: string,
  mode: 'ad-account' | 'page',
): Promise<{ onboarding: AccountOnboarding; resumed: boolean }> {
  return onboardingFetch('/api/onboarding/reconnect', {
    method: 'POST',
    body: JSON.stringify({ accountId, mode }),
  });
}

export function getConnectUrl(id: string): Promise<{ authUrl: string }> {
  return onboardingFetch(`/api/onboarding/${id}/connect-url`);
}

// Forwards Zernio's headless redirect params to the authed callback endpoint,
// which completes the handshake server-side. POST (not GET) so the tempToken
// rides the request BODY — it must not reappear in a URL / Cloud Run log on
// our own hop. Accepts both shapes: the direct (tempToken + userProfile +
// profileId) one facebook sends, and the pendingDataToken fallback.
export function recordCallback(params: {
  onboardingId: string;
  pendingDataToken?: string;
  profileId?: string;
  tempToken?: string;
  userProfile?: string;
  expiresIn?: number;
}): Promise<{ onboarding: AccountOnboarding }> {
  return onboardingFetch('/api/onboarding/callback', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export type OnboardingPagesResult = {
  pages: OnboardingPageOption[];
  available: boolean; // false = no cached list (fetch failed) → show retry
  total: number;
};

// kyck-native Facebook Page picker. q is server-side filtered; results capped
// at 50. refresh=true refetches from Zernio (may throw TokenExpiredError).
export function getOnboardingPages(
  id: string,
  opts: { q?: string; refresh?: boolean } = {},
): Promise<OnboardingPagesResult> {
  const qs = new URLSearchParams();
  if (opts.q) qs.set('q', opts.q);
  if (opts.refresh) qs.set('refresh', '1');
  const suffix = qs.toString();
  return onboardingFetch(`/api/onboarding/${id}/pages${suffix ? `?${suffix}` : ''}`);
}

export function selectOnboardingPage(
  id: string,
  pageId: string,
): Promise<{ onboarding: AccountOnboarding }> {
  return onboardingFetch(`/api/onboarding/${id}/select-page`, {
    method: 'POST',
    body: JSON.stringify({ pageId }),
  });
}

export function getOnboardingAdAccounts(
  id: string,
): Promise<{ adAccounts: OnboardingAdAccount[] }> {
  return onboardingFetch(`/api/onboarding/${id}/ad-accounts`);
}

export function selectOnboardingAdAccount(
  id: string,
  adAccountId: string,
): Promise<{ onboarding: AccountOnboarding }> {
  return onboardingFetch(`/api/onboarding/${id}/select`, {
    method: 'POST',
    body: JSON.stringify({ adAccountId }),
  });
}

export function getOnboardingResources(id: string): Promise<OnboardingResources> {
  return onboardingFetch(`/api/onboarding/${id}/resources`);
}

export function finalizeOnboarding(
  id: string,
  body: FinalizeBody,
): Promise<{ onboarding: AccountOnboarding; account: Account }> {
  return onboardingFetch(`/api/onboarding/${id}/finalize`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------- Bulk onboarding (single-token batch connect) ----------

export type BulkMappingResult = {
  rows: BulkMappingRow[];
  pagesAvailable: boolean;
  adAccountsAvailable: boolean;
  masterPageCount: number;
  masterAdAccountCount: number;
};

export function getBulkSession(): Promise<{ session: BulkSessionStatus; run: BulkRunStatus }> {
  return onboardingFetch('/api/onboarding/bulk/session');
}

export function getBulkConnectUrl(): Promise<{ authUrl: string }> {
  return onboardingFetch('/api/onboarding/bulk/connect-url');
}

export function clearBulkSession(): Promise<{ session: BulkSessionStatus }> {
  return onboardingFetch('/api/onboarding/bulk/session', { method: 'DELETE' });
}

// Forwards the headless authorize params in the POST body (token never in a URL
// on our hop — same hygiene as the per-record callback).
export function recordBulkCallback(params: {
  pendingDataToken?: string;
  profileId?: string;
  tempToken?: string;
  userProfile?: string;
  expiresIn?: number;
  accountId?: string;
}): Promise<{ session: BulkSessionStatus }> {
  return onboardingFetch('/api/onboarding/bulk/callback', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export function getBulkMapping(refresh = false): Promise<BulkMappingResult> {
  return onboardingFetch(`/api/onboarding/bulk/mapping${refresh ? '?refresh=1' : ''}`);
}

export function searchBulkPages(q: string): Promise<{ results: MasterPage[]; total: number }> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : '';
  return onboardingFetch(`/api/onboarding/bulk/pages${qs}`);
}

export function searchBulkAdAccounts(
  q: string,
): Promise<{ results: MasterAdAccount[]; total: number }> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : '';
  return onboardingFetch(`/api/onboarding/bulk/ad-accounts${qs}`);
}

// busy: another chunk holds the run lease, this call did no work (wait, retry).
export type BulkRunStep = BulkRunStepResponse;

// Start a run + process the FIRST request-held chunk inline. Returns the run
// status + whether the whole batch finished in this one chunk. If !done, the
// caller drives continueBulkRun() until done — each call is another held
// request that advances the run (never fire-and-forget, which died on Cloud Run
// instance reclamation).
export function runBulkOnboard(
  clients: Array<{
    accountId: string;
    accountName: string;
    profileName?: string | null;
    pageId: string;
    pageName?: string | null;
    adAccountId: string;
  }>,
  concurrency?: number,
): Promise<BulkRunStep> {
  return onboardingFetch('/api/onboarding/bulk/run', {
    method: 'POST',
    body: JSON.stringify({ clients, ...(concurrency ? { concurrency } : {}) }),
  });
}

// Advance the active run by one more request-held chunk. Idempotent — a
// finished/absent run returns done:true.
export function continueBulkRun(): Promise<BulkRunStep> {
  return onboardingFetch('/api/onboarding/bulk/run/continue', { method: 'POST' });
}

// Discard the run record (clear an interrupted run) without dropping the
// reusable session.
export function clearBulkRun(): Promise<{ run: BulkRunStatus }> {
  return onboardingFetch('/api/onboarding/bulk/run', { method: 'DELETE' });
}

// ---------- GBP connect (Community Inbox Phase 0b) ----------

export type GbpLocationOption = {
  id: string;
  name: string;
  googleAccountId: string | null;
  accountName: string | null;
  address: string | null;
  category: string | null;
  storeCode: string | null;
};

export function getGbpConnectUrl(accountId: string): Promise<{ authUrl: string }> {
  return onboardingFetch('/api/onboarding/gbp/connect-url', {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  });
}

// Tokens ride the body (never the query string) so they aren't re-logged.
export function listGbpLocations(input: {
  accountId: string;
  pendingDataToken?: string;
  tempToken?: string;
  search?: string;
}): Promise<{ locations: GbpLocationOption[]; hasMore: boolean }> {
  return onboardingFetch('/api/onboarding/gbp/locations', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function selectGbpLocation(input: {
  accountId: string;
  pendingDataToken: string;
  locationId: string;
  googleAccountId?: string;
}): Promise<{ connected: true }> {
  return onboardingFetch('/api/onboarding/gbp/select', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ---------- Instagram connect (per-account, standalone OAuth) ----------

export function getInstagramConnectUrl(accountId: string): Promise<{ authUrl: string }> {
  return onboardingFetch('/api/onboarding/instagram/connect-url', {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  });
}

export function finalizeInstagramConnect(input: {
  accountId: string;
  username?: string;
}): Promise<{ connected: true; instagram: { id: string | null; username: string | null } }> {
  return onboardingFetch('/api/onboarding/instagram/callback', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ---------- Google Ads connect + picker (per-account, standalone OAuth) ----------

export function getGoogleAdsConnectUrl(
  accountId: string,
): Promise<{ authUrl: string | null; alreadyConnected: boolean }> {
  return onboardingFetch('/api/onboarding/google-ads/connect-url', {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  });
}

// Enumerate the Google Ads accounts for the picker. The list rides Zernio's
// SHARED quota, so the response carries quotaLimited/stale/available so the picker
// can degrade gracefully; refresh forces a live re-fetch.
export function listGoogleAdsAccounts(
  accountId: string,
  refresh = false,
): Promise<GoogleAdsAccountListResult> {
  const qs = new URLSearchParams({ accountId });
  if (refresh) qs.set('refresh', '1');
  return onboardingFetch(`/api/onboarding/google-ads/accounts?${qs}`);
}

export function selectGoogleAdsAccount(input: {
  accountId: string;
  customerId: string;
}): Promise<GoogleAdsSelectResult> {
  return onboardingFetch('/api/onboarding/google-ads/select', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ---------- GBP AGENCY onboarding v2 (assign-location) ----------

// The grant sources — accounts whose GBP is already connected (via the per-account
// button). The operator picks which one to assign through.
export function getGbpTokenHolders(): Promise<{ tokenHolders: GbpTokenHolder[] }> {
  return onboardingFetch('/api/onboarding/gbp-agency/token-holders');
}

export function getGbpMappingAccounts(): Promise<{ accounts: GbpMappingAccount[] }> {
  return onboardingFetch('/api/onboarding/gbp-agency/accounts');
}

export function listGbpAgencyLocations(
  tokenHolderAccountId: string,
  search?: string,
): Promise<{ locations: GbpAgencyLocation[]; hasMore: boolean }> {
  const qs = new URLSearchParams({ tokenHolderAccountId });
  if (search) qs.set('search', search);
  return onboardingFetch(`/api/onboarding/gbp-agency/locations?${qs}`);
}

// Assign ONE account to a location via the token holder's grant. A 409 comes back
// as { status: 'needs_switch' } so the caller can offer the switch.
export function assignGbpAccount(input: {
  tokenHolderAccountId: string;
  targetAccountId: string;
  locationId: string;
  googleAccountId?: string;
  locationName?: string;
}): Promise<GbpAssignResult> {
  return onboardingFetch('/api/onboarding/gbp-agency/assign', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// The 409 → switch action (updateGmbLocation on the account's own connection).
export function switchGbpAccount(input: {
  targetAccountId: string;
  locationId: string;
  googleAccountId?: string;
  locationName?: string;
}): Promise<GbpAssignResult> {
  return onboardingFetch('/api/onboarding/gbp-agency/switch', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// Scoped review re-ingest for just-assigned accounts (correct-attribution fix).
export function reingestReviewsForAccounts(accountIds: string[]): Promise<unknown> {
  return onboardingFetch('/api/community/reviews/reingest', {
    method: 'POST',
    body: JSON.stringify({ accountIds }),
  });
}
